import cx from 'classnames';
import React from 'react';

import type {
    AriaMenuItem,
    ButtonProps,
    LogoSizeModifier,
} from '@pexip/components';
import {BadgeCounter, useBelow360, useMobileDevice} from '@pexip/components';

import {TestId} from '../..';
import type {DeviceStatusInfo} from '../../types';
import {PanelHeader} from '../PanelHeader/PanelHeader.view';

import type {AudioButtonProps} from './Controls/AudioButton.view';
import {AudioButton} from './Controls/AudioButton.view';
import type {LeaveButtonDesktopProps} from './Controls/LeaveButton.view';
import {LeaveButtonTouch} from './Controls/LeaveButton.view';
import {MoreMenu} from './Controls/MoreMenu.view';
import type {PresentationButtonProps} from './Controls/PresentationButton.view';
import {PresentationButton} from './Controls/PresentationButton.view';
import type {RaiseHandButtonProps} from './Controls/RaiseHandButton.view';
import {RaiseHandButton} from './Controls/RaiseHandButton.view';
import type {VideoButtonProps} from './Controls/VideoButton.view';
import {VideoButton} from './Controls/VideoButton.view';

import styles from './CallControls.module.scss';

export const CallControlsTouchView: React.FC<
    React.PropsWithChildren<
        React.ComponentProps<'div'> &
            Omit<RaiseHandButtonProps, 'iconSize'> &
            Omit<
                AudioButtonProps,
                'iconSize' | 'isDisabled' | 'canMute' | 'requestDeviceAgain'
            > &
            Omit<
                VideoButtonProps,
                'iconSize' | 'isDisabled' | 'canMute' | 'requestDeviceAgain'
            > &
            Omit<LeaveButtonDesktopProps, 'iconSize'> &
            Omit<PresentationButtonProps, 'iconSize'> & {
                ableToPresent: boolean;
                allowedToPresent: boolean;
                audioRequestDeviceAgain: () => void;
                canMuteAudio?: boolean;
                clearPluginMenu: () => void;
                isAudioMuteDisabled?: boolean;
                isMoreMenuBadgeCounterVisible?: boolean;
                isRequestingAudio: boolean;
                isRequestingVideo: boolean;
                isVideoMuteDisabled?: boolean;
                moreMenuItems: AriaMenuItem[];
                onMoreMenuOpenChange: (isOpen: boolean) => void;
                pluginMenuParent?: string;
                presentationDisabledText: string;
                presentationModeToggle?: React.ReactNode;
                isAudioError: boolean;
                isVideoError: boolean;
                isBothError: boolean;
                audioTooltipVisible: boolean;
                videoTooltipVisible: boolean;
                toggleAudioTooltip: () => void;
                toggleVideoTooltip: () => void;
                audioStatusInfo?: DeviceStatusInfo;
                videoStatusInfo?: DeviceStatusInfo;
                videoRequestDeviceAgain: () => void;
            }
    >
> = ({
    ableToPresent,
    allowedToPresent,
    canMuteAudio = true,
    clearPluginMenu,
    inputStatusInfo,
    isAudioMuteDisabled,
    isHandRaised,
    isMicrophoneMuted,
    isMoreMenuBadgeCounterVisible: isBadgeCounterVisible = false,
    isRaiseHandHidden,
    isRequestingAudio,
    isRequestingVideo,
    isVideoMuteDisabled,
    isVideoMuted,
    localIsPresenting,
    moreMenuItems,
    onAudioBtnClick,
    onLeave,
    onMoreMenuOpenChange,
    onRaiseHand,
    onVideoBtnClick,
    pluginMenuParent,
    presentationDisabledText,
    presentationModeToggle,
    videoRequestDeviceAgain,
    startPresentation,
    stopPresentation,
    isAudioError,
    isVideoError,
    isBothError,
    audioTooltipVisible,
    videoTooltipVisible,
    toggleAudioTooltip,
    toggleVideoTooltip,
    audioStatusInfo,
    audioRequestDeviceAgain,
    videoStatusInfo,
    ...props
}) => {
    const isMobileDevice = useMobileDevice();
    const isCoreOnlyTouchCallControls = useBelow360();
    const iconSize: LogoSizeModifier = isMobileDevice ? 'medium' : 'small';
    const buttonVariant: ButtonProps['variant'] = 'neutral';

    return (
        <div
            className={cx(styles.callControlsWrapper, styles.touchWrapper)}
            {...props}
        >
            <div className={styles.touchCallControlBtnWrapper}>
                <MoreMenu
                    isTouch
                    isMobile={isMobileDevice}
                    items={moreMenuItems}
                    onOpenChange={onMoreMenuOpenChange}
                    headerContent={
                        pluginMenuParent ? (
                            <PanelHeader
                                title={pluginMenuParent}
                                onBackClick={clearPluginMenu}
                                backButtonTestId={
                                    TestId.ButtonCallControlsTouchMoreMenuBack
                                }
                            />
                        ) : undefined
                    }
                >
                    <BadgeCounter
                        variant="info"
                        isTruncated
                        isVisible={isBadgeCounterVisible}
                        badgeClassName={styles.moreMenuBadge}
                    />
                </MoreMenu>
                {presentationModeToggle}
                {!isCoreOnlyTouchCallControls && (
                    <RaiseHandButton
                        variant={buttonVariant}
                        isHandRaised={isHandRaised}
                        onRaiseHand={onRaiseHand}
                        iconSize={iconSize}
                        isRaiseHandHidden={isRaiseHandHidden}
                    />
                )}
                {ableToPresent && (
                    <PresentationButton
                        variant={buttonVariant}
                        localIsPresenting={localIsPresenting}
                        iconSize={iconSize}
                        stopPresentation={stopPresentation}
                        startPresentation={startPresentation}
                        isDisabled={!allowedToPresent}
                        disabledReason={presentationDisabledText}
                    />
                )}

                <AudioButton
                    canMute={canMuteAudio}
                    hasError={isAudioError || isBothError}
                    iconSize={iconSize}
                    inputStatusInfo={audioStatusInfo}
                    isDisabled={isAudioMuteDisabled}
                    isLoading={isRequestingAudio}
                    isMicrophoneMuted={isMicrophoneMuted}
                    onAudioBtnClick={onAudioBtnClick}
                    requestDeviceAgain={audioRequestDeviceAgain}
                    shouldShowTooltip={audioTooltipVisible}
                    toggleTooltip={toggleAudioTooltip}
                    variant={buttonVariant}
                />
                <VideoButton
                    hasError={isVideoError}
                    iconSize={iconSize}
                    inputStatusInfo={videoStatusInfo}
                    isDisabled={isVideoMuteDisabled}
                    isLoading={isRequestingVideo}
                    isVideoMuted={isVideoMuted}
                    onVideoBtnClick={onVideoBtnClick}
                    requestDeviceAgain={videoRequestDeviceAgain}
                    shouldShowTooltip={videoTooltipVisible}
                    toggleTooltip={toggleVideoTooltip}
                    variant={buttonVariant}
                />
                <LeaveButtonTouch onLeave={onLeave} iconSize={iconSize} />
            </div>
        </div>
    );
};

export type CallControlsTouchProps = React.ComponentProps<
    typeof CallControlsTouchView
>;
